import { useMemo, useState } from 'react';
import { FUNCTION_CATALOG } from '@/lib/excelFunctionLibrary';

interface ExcelFunctionBrowserProps {
  open: boolean;
  onClose: () => void;
  onPick: (name: string) => void;
}

/** Navigateur de fonctions du tableur (recherche + catégories). */
export default function ExcelFunctionBrowser({ open, onClose, onPick }: ExcelFunctionBrowserProps) {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('Toutes');

  const categories = useMemo(
    () => ['Toutes', ...Array.from(new Set(FUNCTION_CATALOG.map((f) => f.category)))],
    [],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return FUNCTION_CATALOG.filter((f) => {
      const inCategory = category === 'Toutes' || f.category === category;
      if (!inCategory) return false;
      if (!q) return true;
      return (
        f.name.toLowerCase().includes(q) ||
        f.label.toLowerCase().includes(q) ||
        f.syntax.toLowerCase().includes(q)
      );
    });
  }, [search, category]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative w-full max-w-2xl max-h-[80vh] bg-background-50 rounded-xl flex flex-col overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-3.5 border-b border-background-200/70">
          <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center">
            <i className="ri-function-line"></i>
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground-950">Insérer une fonction</p>
            <p className="text-[11px] text-foreground-400">
              {FUNCTION_CATALOG.length} fonctions disponibles · cliquez pour insérer
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full text-foreground-500 hover:bg-background-100 flex items-center justify-center cursor-pointer"
            title="Fermer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="px-5 py-3 space-y-2 border-b border-background-200/70">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher une fonction (ex. SOMME.SI, RECHERCHEV…)"
              autoFocus
              className="w-full pl-9 pr-3 py-2 bg-white border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-accent-300"
            />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium cursor-pointer transition-colors whitespace-nowrap ${
                  category === c
                    ? 'bg-foreground-900 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {filtered.length === 0 && (
            <p className="text-center text-sm text-foreground-400 py-10">Aucune fonction trouvée.</p>
          )}
          {filtered.map((f) => (
            <button
              key={f.name}
              onClick={() => onPick(f.name)}
              className="w-full text-left px-3 py-2 rounded-lg hover:bg-background-100 cursor-pointer transition-colors"
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold text-foreground-900">{f.name}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-900">{f.category}</span>
              </div>
              <p className="text-[11px] text-foreground-500 mt-0.5">{f.label}</p>
              <code className="text-[11px] text-accent-700">{f.syntax}</code>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}